import { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useService } from '@atlassian/jira-expansion-services/src/index.tsx';
import { useCurrentProduct } from '@atlassian/jira-invite-prompt-on-link-insert/src/utils/index.tsx';
import { requestAccess as requestAccessExpansionService } from '@atlassian/expansion-services/services';

import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import type { AccessRequestType } from '../../common/types.tsx';
import { useMsrRequestAccessErrorFlag } from '../request-access-error-flag/msr-request-access-error-flag.tsx';
import { useMsrRequestAccessSuccessFlag } from '../request-access-success-flag/msr-request-access-success-flag.tsx';
import {
	useMsrRequestAccessPromptFlag,
	getAnalyticsAttributes,
} from '../request-access-prompt-flag/msr-request-access-flag.tsx';
import { setHasSeenNudgeInLast7Days } from '../../services/caching/index.tsx';
import { RECOMMENDATION_PRODUCT_KEYS } from '../../services/recommendations/index.tsx';

const MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS = 5;
const CROSS_JOIN_NUDGES_JIRA_TO_CONFLUENCE_INTEGRATION = 'crossJoinNudgesJiraToConfluence';

export const useMsrConfluenceCrossJoinNudge = ({ sessionId }: { sessionId: string }) => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const currentProduct = useCurrentProduct();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'confluence',
		MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS,
	);
	const showMsrRequestAccessPromptFlag = useMsrRequestAccessPromptFlag();
	const showSuccessFlag = useMsrRequestAccessSuccessFlag();
	const showErrorFlag = useMsrRequestAccessErrorFlag();
	const { push } = useRouterActions();

	const requestAccessService = fg('expansion_services_in_cjn')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useService(requestAccessExpansionService)
		: null;

	return useCallback(
		async ({ recommendations }: { recommendations: string[] }) => {
			try {
				const collaboratorsOnConfluence = await getFilteredCollaborators();
				async function onSubmit() {
					try {
						try {
							let accessStatus: AccessRequestType;
							if (requestAccessService && fg('expansion_services_in_cjn')) {
								const serviceResult = await requestAccessService({
									product: 'confluence',
									integration: 'crossJoinNudgesConfluence',
								});

								if (serviceResult.error) {
									fireErrorAnalytics('confluenceRequestAccess', serviceResult.error);
									throw serviceResult.error;
								}
								accessStatus = serviceResult.result;
							} else {
								accessStatus = await requestAccess(
									'confluence',
									currentProduct,
									cloudId,
									userId,
									'crossJoinNudgesConfluence',
									createAnalyticsEvent,
								);
							}
							if (
								accessStatus === 'ACCESS_GRANTED' ||
								accessStatus === 'ACCESS_EXISTS' ||
								accessStatus === 'APPROVED_REQUEST_EXISTS'
							) {
								push('/wiki');
							} else if (
								accessStatus === 'PENDING_REQUEST_CREATED' ||
								accessStatus === 'PENDING_REQUEST_EXISTS'
							) {
								showSuccessFlag();
							} else {
								showErrorFlag('confluence');
							}
						} catch {
							showErrorFlag('confluence');
						}
					} catch {
						showErrorFlag('confluence');
					}
				}

				showMsrRequestAccessPromptFlag({
					users: collaboratorsOnConfluence,
					onSubmit,
					targetProduct: 'confluence',
					integration: CROSS_JOIN_NUDGES_JIRA_TO_CONFLUENCE_INTEGRATION,
					eventAttributes: getAnalyticsAttributes({
						userId,
						users: collaboratorsOnConfluence,
						integration: CROSS_JOIN_NUDGES_JIRA_TO_CONFLUENCE_INTEGRATION,
						recommendationSessionId: sessionId,
						nudgeId: RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE,
						recommendations,
					}),
				});

				// this is used in the eligibility check for loom nudge
				await setHasSeenNudgeInLast7Days();

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics('showMsrNudge', error, {
					cloudId,
					targetProduct: 'confluence',
					integration: CROSS_JOIN_NUDGES_JIRA_TO_CONFLUENCE_INTEGRATION,
				});
			}
		},
		[
			cloudId,
			userId,
			currentProduct,
			createAnalyticsEvent,
			getFilteredCollaborators,
			push,
			requestAccessService,
			showErrorFlag,
			showSuccessFlag,
			showMsrRequestAccessPromptFlag,
			sessionId,
		],
	);
};
